import { cn } from "@/lib/utils";
import { ITEM_PLACEHOLDER_IMAGE, ITEM_PLACEHOLDER_MARK_ZOOM } from "./item-image-box-sizes";

/**
 * The donor's generic "no picture" mark, drawn at the size modsy draws it.
 *
 * **Why this is not just an `<img>`.** `placeholder-image.png` is a 1600x900
 * field with a small centred mark — the mark is a 192px square, 12% of the
 * canvas width — because the donor uses it full-bleed behind a large slot. Modsy's
 * *failed-image* graphic is a different file: the 401x400 body its CDN returns
 * under a 403, whose ink spans 284x266 — 71% of that canvas — and so lands at
 * 19.8 x 18.6 px in a 28px tile, leaving a few pixels of ground all round
 * (measured on the running stack, #496).
 *
 * Dropped into a 32px tile as an ordinary picture, our asset's mark renders
 * around 6px — under a third of what a modsy user sees. So this crops to the
 * mark instead of scaling the whole field: a background sized to
 * `ITEM_PLACEHOLDER_MARK_ZOOM` shows the mark's square, centred, at the size the
 * donor's glyph occupies — using the donor's own in-repo asset and no redrawn
 * artwork. It is a downscale (192px source into a ~20px slot), so it stays
 * crisp.
 *
 * What this deliberately does *not* reproduce is the CDN glyph's drawing — the
 * diagonal slash and the sun. That is artwork, not geometry, and matching it
 * would mean porting a file that exists only as CloudFront error-page
 * configuration. Carried to #496's decision round instead.
 */
export function ItemPlaceholderMark({
  className,
  label,
  zoom = ITEM_PLACEHOLDER_MARK_ZOOM,
}: {
  className?: string;
  /**
   * The accessible name, where the slot needs one.
   *
   * Omitted where an enclosing control already names it — the Add Variant
   * dialog's tile, whose button carries the label and whose donor draws this as
   * a CSS background with no alternative text at all.
   */
  label?: string;
  /**
   * The crop, for a slot that is not the donor's 28px square tile.
   *
   * Always from `itemPlaceholderMarkZoom(w, h)`, never a literal: a percentage
   * sizes the drawn image by width, while modsy fits its glyph with
   * `object-fit: contain`, so the right number depends on the box's shape. The
   * default is the square tile's, which both variant strips use.
   */
  zoom?: string;
}) {
  return (
    <div
      data-testid="item-placeholder-mark"
      role={label ? "img" : "presentation"}
      aria-label={label}
      className={cn("bg-center bg-no-repeat", className)}
      style={{
        backgroundImage: `url(${ITEM_PLACEHOLDER_IMAGE})`,
        backgroundSize: zoom,
      }}
    />
  );
}
